import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { erfuelltRecht } from '@symphenia/shared-types';
import { useAuthStore } from '@/lib/auth.store';

interface ProtectedRouteProps {
  /**
   * AP-30-D — Das Recht, das diese Route verlangt.
   *
   * Ersetzt `requiredRole` **und** `allowedRoles`. Die beiden gab es nur, weil
   * eine Leiter „MANAGER **oder** READONLY" nicht ausdrücken kann: Die
   * Untergrenze hätte READONLY (unterhalb von EMPLOYEE) mit hereingelassen oder
   * MANAGER ausgesperrt. Genau dieser laterale Fall ist der Grund für AP-30 —
   * und mit Rechten ist er eine einzige Angabe: `time.entry.read`, erfüllt von
   * beiden.
   *
   * Mehrere Rechte heißen **UND**, wie am Endpunkt.
   */
  requiredPermission?: string | string[];
  /**
   * Hier gibt es **kein** `requiredModule` (AP-29-D).
   *
   * Bis dahin hing der Modul-Schutz an der einzelnen Route — an genau einer von
   * rund fünfzig, weil eine neue Unterseite die Eigenschaft regelmäßig vergisst.
   * Er hängt jetzt am **Pfad-Ast**: `ModuleGate` leitet aus dem Pfad das Modul
   * ab (Registry-Feld `areas`) und zeigt bei fehlendem Zugang die Seite „kein
   * Zugang" statt wortlos aufs Dashboard umzuleiten.
   *
   * Zwei Mechanismen für dieselbe Sache wären der Anfang der nächsten Lücke —
   * deshalb ist diese Eigenschaft weg und kommt nicht zurück.
   */
  redirectTo?: string;
}

export function ProtectedRoute({ requiredPermission, redirectTo = '/login' }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <span className="text-muted-foreground text-sm">Loading…</span>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (requiredPermission && user) {
    const verlangt = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    // Dieselbe Funktion wie im `PermissionGuard`. Ein zweiter Rechenweg wäre die
    // erste Abweichung zwischen „die Seite ist da" und „der Endpunkt lässt durch".
    if (!verlangt.every((p) => erfuelltRecht(p, user.permissions))) {
      return <Navigate to="/" replace />;
    }
  }

  return <Outlet />;
}
